import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type JSX,
  type ReactNode,
} from 'react';
import type { Asset } from '@taki/data-model';
import {
  Button,
  IconCameraVideo,
  Input,
  Modal,
  ScreenLayout,
  ViewHost,
  usePendingAppAction,
  usePref,
  useScreen,
} from '@taki/ui';
import type {
  ScreenDoor,
  ScreenFavorite,
  ScreenFilterDef,
  ScreenSortOption,
  ScreenStat,
} from '@taki/ui';
import { useTakiApp } from '../../App.js';
import {
  INVENTAIRE_APP_ID,
  INVENTAIRE_SCREEN_ID,
  INVENTAIRE_VIEW_TYPES,
  createInventaireBindings,
} from './views.js';
import {
  CATEGORY_OPTIONS,
  CONDITION_OPTIONS,
  MATERIEL_TYPE,
  categoryOf,
  customNumber,
  formatEuros,
  isMateriel,
  statusOf,
} from './data.js';
import {
  DEFAULT_FILTERS,
  DEFAULT_STATS,
  FILTER_DEFS,
  FILTERS_PREF_KEY,
  STAT_DEFS,
  STATS_MAX,
  STATS_PREF_KEY,
  type InventaireStatId,
} from './settings-defs.js';

/**
 * Ecran Parc de l'app `inventaire` : la vue d'ensemble du materiel de location,
 * avec une fiche d'identite complete par exemplaire. App de REFERENCE du
 * gabarit v2 pour les ecrans A PLUSIEURS PORTES.
 *
 * DONNEES : famille `assets` du socle, specialisee par `type: 'materiel'` et
 * par `customFields` (aucune entite nouvelle). Toute lecture filtre sur
 * `asset.type === 'materiel'` : le parc coexiste dans l'org avec les assets
 * `cat` des apps de demo existantes, qui ne doivent JAMAIS apparaitre ici.
 *
 * LES PORTES (zone 1). Le menu n'est plus la segmentation d'une collection :
 * chaque entree est une PORTE, un JEU DE DONNEES de l'app (decision fondateur
 * du 01/08/2026 : la zone 1 dit QUEL JEU, la zone 4 dit QUELLE TRANCHE).
 * L'app declare les TROIS portes du plan canonique du fondateur (Materiel /
 * Livraisons / Maintenance, spec du 04/08/2026) :
 * - MATERIEL : tout le parc. C'est l'ancienne entree "Tout le parc" ;
 * - LIVRAISONS : jeu VIDE pour l'instant (le suivi des livraisons est un
 *   chantier a part), et la demonstration d'une porte SANS TRANCHES
 *   (`sections: false`) : compteur 0 gele, etoile masquee, et le groupe
 *   d'outils garde le tri ET l'outil filtre, qui ne porte alors que l'entree
 *   Archives (G20). Le tiroir n'apparait que le temps du mode Archives, avec
 *   le seul tag de mode ;
 * - MAINTENANCE : les exemplaires dont la disponibilite est "En maintenance",
 *   le sous-ensemble qu'un loueur surveille a part.
 * Les portes partagent le meme chargeur (elles se distinguent par
 * `query.section`), les memes vues et les memes bindings : ce sont les memes
 * materiels, racontes pareil. Changer de porte remet a zero recherche, filtres
 * et tri, referme la fiche et recharge : c'est le moteur qui le garantit.
 *
 * LES CATEGORIES (camera, optique, lumiere, son, machinerie), qui etaient le
 * menu de la V1, sont DESCENDUES dans le CATALOGUE DE FILTRES des portes :
 * multi-choix additif dans le popover de l'outil filtre (3b), un filtre actif
 * = un tag retirable dans le tiroir (4a). Poser "Camera" puis "Optique"
 * AGRANDIT la selection (un OU, jamais un ET) : le vrai besoin d'un loueur
 * ("montre-moi le materiel image"). Les anciennes pastilles de disponibilite
 * n'ont plus de raison d'etre : la disponibilite est une PORTE (Maintenance),
 * une cle de TRI ("Etat") et des STATS, exactement le decoupage du proto.
 *
 * Ce que chaque porte apporte (l'ecran repasse tout via `screen.door.*`) :
 * ses actions (3a, l'ajout declare `kind: 'add'` est rendu "+ Ajouter" par le
 * chassis, regle G5), ses stats (2c, forme fonction sur le jeu VIVANT de la
 * porte : insensibles aux filtres et a la recherche), son placeholder de
 * recherche (2b), et ses FAVORIS PRESETS (4c, les quatre du proto, portes par
 * la seule porte Materiel : une liste de favoris est PAR PORTE).
 *
 * Structure imposee par la cage IA (docs/GABARIT-ECRAN.md) :
 * - UN seul `ScreenLayout` a la racine, le `Modal` de CREATION en FRERE
 *   (regle G6 : la CREATION s'ouvre dans un Modal, l'EDITION se fait EN PLACE
 *   sur la fiche, sans bouton "Modifier", sans brouillon et sans etat "sale") ;
 * - zone 5 pilotee par les VUES du socle : l'ecran ne dessine ni liste, ni
 *   tableau, ni kanban. Il declare CE QUE SONT ses donnees (bindings partages
 *   dans views.ts) et monte un `ViewHost` ;
 * - les etats standard (chargement, erreur, vide, fiche introuvable) vont au
 *   `ViewHost`, jamais au gabarit ;
 * - acces data UNIQUEMENT via `@taki/sdk` (`useTakiApp`), aucune vue ne recoit
 *   jamais le SDK.
 *
 * Les compteurs du menu se calculent sur le jeu COMPLET (memorise a chaque
 * chargement), jamais sur la porte courante : un menu dont les compteurs
 * disparaissent quand on clique dedans est un bug.
 */

/* ------------------------------------------------------------------ */
/* Les portes et leurs catalogues                                      */
/* ------------------------------------------------------------------ */

/** Ids des trois portes. Ils voyagent dans le parametre `s` de l'adresse. */
const MATERIEL_DOOR_ID = 'materiel';
const LIVRAISONS_DOOR_ID = 'livraisons';
const MAINTENANCE_DOOR_ID = 'maintenance';

/**
 * LE CATALOGUE DE TRI de l'ecran (popover de l'outil tri, 3b). C'est une
 * declaration d'ecran, pas un etat : le moteur ne le connait pas. Il couvre
 * TOUTES les cles triables (les roles `title` et `status`, puis les colonnes
 * `sortable`), parce que la pile de tri est UNIQUE pour toutes les vues : un
 * tri pose par l'en-tete du tableau doit rendre un tag lisible ici aussi.
 */
const SORT_OPTIONS: ScreenSortOption[] = [
  { key: 'title', label: 'Nom' },
  { key: 'status', label: 'Etat' },
  { key: 'category', label: 'Categorie' },
  { key: 'reference', label: 'Reference' },
  { key: 'dailyRate', label: 'Tarif / jour' },
  { key: 'purchasePrice', label: "Valeur d'achat" },
  { key: 'purchaseDate', label: 'Achete le' },
];

/**
 * Les FAVORIS PRESETS de la porte Materiel (zone 4c) : les quatre etats
 * nommes du proto. Un favori n'est jamais une donnee metier, seulement une
 * combinaison de filtres et de tris REELS (cles de SORT_OPTIONS, ids de
 * FILTER_DEFS). Ils sont servis avant les favoris que l'utilisateur garde
 * (bouton 4b), et ne se retirent pas. La porte Maintenance n'en declare
 * aucun : une liste de favoris est PAR PORTE (le proto montrait les memes
 * partout, simplification de proto a ne pas reproduire).
 */
const MATERIEL_PRESETS: ScreenFavorite[] = [
  { id: 'tout', label: 'Tout le parc', filterIds: [], sortStack: [{ key: 'title', dir: 'asc' }] },
  {
    id: 'image',
    label: 'Materiel image',
    filterIds: ['camera', 'optique'],
    sortStack: [
      { key: 'category', dir: 'asc' },
      { key: 'title', dir: 'asc' },
    ],
  },
  {
    id: 'par-statut',
    label: 'Par statut',
    filterIds: [],
    sortStack: [
      { key: 'status', dir: 'asc' },
      { key: 'title', dir: 'asc' },
    ],
  },
  {
    id: 'par-reference',
    label: 'Par reference',
    filterIds: [],
    sortStack: [{ key: 'reference', dir: 'asc' }],
  },
];

/* ------------------------------------------------------------------ */
/* Le formulaire de CREATION (Modal) : brouillon et SDK                */
/* ------------------------------------------------------------------ */

/** Le brouillon du formulaire : que du texte, comme les champs du DOM. */
interface MaterielDraft {
  name: string;
  category: string;
  brand: string;
  model: string;
  reference: string;
  purchaseDate: string;
  purchasePrice: string;
  dailyRate: string;
  condition: string;
  storage: string;
}

const EMPTY_DRAFT: MaterielDraft = {
  name: '',
  category: 'camera',
  brand: '',
  model: '',
  reference: '',
  purchaseDate: '',
  purchasePrice: '',
  dailyRate: '',
  condition: 'bon',
  storage: '',
};

/** Un montant saisi, ou `null` si le champ est vide ou illisible. */
function toNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Le sac `customFields` d'un materiel NEUF, depuis le brouillon.
 *
 * On n'ecrit que ce qui est renseigne : pas de cles vides parasites dans un
 * document tout neuf. Vider une cle, c'est le geste de la FICHE, pas celui de
 * la creation (cf. `writeCustomField` dans views.ts, qui envoie `null` et
 * supprime la cle).
 */
function customFieldsFromDraft(draft: MaterielDraft): Record<string, string | number> {
  const fields: Record<string, string | number> = {};
  const put = (key: string, value: string | number | null): void => {
    if (value === null || value === '') return;
    fields[key] = value;
  };
  put('category', draft.category.trim());
  put('brand', draft.brand.trim());
  put('model', draft.model.trim());
  put('purchaseDate', draft.purchaseDate.trim());
  put('purchasePrice', toNumber(draft.purchasePrice));
  put('dailyRate', toNumber(draft.dailyRate));
  put('condition', draft.condition.trim());
  put('storage', draft.storage.trim());
  return fields;
}

/**
 * Habillage d'un champ du formulaire : libelle au-dessus, controle dessous, et
 * une precision optionnelle en dessous quand le champ a une contrainte que
 * l'utilisateur ne peut pas deviner.
 */
function Field({
  label,
  hint,
  wide = false,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className={wide ? 'col-span-2 flex flex-col gap-1.5' : 'flex flex-col gap-1.5'}>
      <span className="text-[0.75rem] font-medium text-ink-500">{label}</span>
      {children}
      {hint != null ? <span className="text-[0.6875rem] text-ink-400">{hint}</span> : null}
    </label>
  );
}

/**
 * Les listes deroulantes reprennent exactement l'habillage du champ `Input` de
 * @taki/ui (memes tokens, memes rem) : @taki/ui n'expose pas encore de
 * composant Select, et une app ne dessine pas le sien.
 */
const SELECT_CLASSES =
  'h-[3.125rem] w-full rounded-[0.8125rem] border border-border bg-surface-1 px-4 text-sm ' +
  'text-ink-900 font-sans transition-colors hover:border-border-strong ' +
  'focus-visible:outline-none focus-visible:border-accent-border focus-visible:shadow-ring-soft';

/* ------------------------------------------------------------------ */
/* L'ecran                                                             */
/* ------------------------------------------------------------------ */

export function InventaireScreen(): JSX.Element {
  const { sdk, config } = useTakiApp();

  // Prefs partagees avec settings.tsx (hub Parametres du shell). L'ecran LIT,
  // le hub EDITE ; l'event "taki:pref-change" resynchronise en direct.
  const [filterVisibility] = usePref<Record<string, boolean>>(FILTERS_PREF_KEY, DEFAULT_FILTERS);
  const [statIds] = usePref<string[]>(STATS_PREF_KEY, DEFAULT_STATS);

  // Le jeu COMPLET du parc, memorise a chaque chargement : il alimente les
  // compteurs du menu, qui ne doivent jamais dependre de la porte courante.
  const [parc, setParc] = useState<Asset[]>([]);

  // Le catalogue reellement declare par les portes : les categories visibles
  // selon la pref (cle absente = visible, seul un `false` explicite masque).
  const filterDefs = useMemo<ScreenFilterDef<Asset>[]>(
    () =>
      FILTER_DEFS.filter((def) => filterVisibility[def.id] !== false).map((def) => ({
        id: def.id,
        label: def.label,
        match: (asset: Asset) => categoryOf(asset) === def.id,
      })),
    [filterVisibility],
  );

  // Les bindings : fabriques UNE fois pour ce SDK (fabrique partagee de
  // views.ts, la meme que celle des reglages). Le SDK vient du contexte de
  // l'hote, il ne change jamais pendant la vie de l'ecran.
  const bindings = useMemo(() => createInventaireBindings(sdk), [sdk]);

  /* ---- Le formulaire de CREATION, dans un Modal (regle G6) ---- */

  /*
   * UN SEUL Modal, et il ne sert qu'a CREER. MODIFIER un materiel se fait
   * EN PLACE sur la fiche (clic sur un champ, ecriture a la sortie) : plus de
   * bouton "Modifier", plus de brouillon pre-rempli, plus d'etat "sale", donc
   * aucune confirmation de sortie a ecrire. Declare AVANT les portes : l'action
   * d'ajout de la porte Materiel l'ouvre.
   */
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState<MaterielDraft>(EMPTY_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const closeModal = useCallback(() => {
    setCreateOpen(false);
    setDraft(EMPTY_DRAFT);
    setFormError(null);
  }, []);

  const openCreate = useCallback(() => {
    setDraft(EMPTY_DRAFT);
    setFormError(null);
    setCreateOpen(true);
  }, []);

  /*
   * LES TROIS PORTES de l'ecran (zone 1). Le tableau est recompose a chaque
   * rendu (le moteur le lit par ref) : les closures y restent fraiches, et les
   * stats en forme fonction recoivent le jeu VIVANT de la porte (archives
   * exclues, AVANT recherche et filtres) a chaque rendu.
   *
   * Les actions sont des DECLARATIONS, sans etat de chargement : "+ Ajouter"
   * reste actif pendant un chargement (le Modal flotte au-dessus de tous les
   * etats de la zone 5). Pas d'action Rafraichir : le rechargement appartient
   * au socle (G19), une action d'app AGIT sur les donnees.
   *
   * La porte MAINTENANCE ne declare PAS d'ajout : un materiel n'entre pas en
   * maintenance par creation, il y entre quand sa disponibilite change (kanban
   * ou fiche). Creer depuis cette porte fabriquerait un exemplaire qui
   * disparait aussitot (il nait "Disponible") : le geste serait un mensonge.
   */
  const doors: ScreenDoor<Asset>[] = [
    {
      id: MATERIEL_DOOR_ID,
      label: 'Materiel',
      filters: filterDefs,
      actions: [
        {
          id: 'new',
          // Libelle IGNORE par le chassis : une action `kind: 'add'` est
          // rendue "+ Ajouter" (universel), en premier, en lime, IconPlus (G5).
          kind: 'add',
          label: 'Ajouter',
          onClick: openCreate,
        },
      ],
      // Stats de la porte : la pref choisit LESQUELLES afficher (3 max, ordre
      // du catalogue), les valeurs restent calculees des donnees reelles.
      stats: (live: Asset[]): ScreenStat[] => {
        const countBy = (status: string): number =>
          live.filter((asset) => statusOf(asset) === status).length;
        const sumOf = (key: string): number =>
          live.reduce((total, asset) => total + (customNumber(asset, key) ?? 0), 0);
        const values: Record<InventaireStatId, string> = {
          total: String(live.length),
          disponible: String(countBy('disponible')),
          loue: String(countBy('loue')),
          maintenance: String(countBy('maintenance')),
          valeur: formatEuros(sumOf('purchasePrice')),
          'tarif-jour': formatEuros(sumOf('dailyRate')),
        };
        return STAT_DEFS.filter((def) => statIds.includes(def.id))
          .slice(0, STATS_MAX)
          .map((def) => ({ value: values[def.id], label: def.label }));
      },
      searchPlaceholder: 'Rechercher un materiel ou demander a Lia...',
      presets: MATERIEL_PRESETS,
    },
    {
      /*
       * LIVRAISONS : la porte du plan canonique qui demontre `sections: false`
       * (aucune tranche : l'etoile est masquee par le gabarit, l'outil FILTRE
       * reste rendu et ne porte que l'entree Archives (G20), et le tiroir
       * n'apparait que le temps du mode Archives) et le compteur 0 GELE du
       * menu. Son jeu est VIDE par construction : le suivi des
       * livraisons (famille `deliveries` du socle) est un chantier a part.
       * Ni action ni stat en attendant : un "+ Ajouter" qui ne cree rien
       * serait un mensonge, et une stat a zero une decoration.
       */
      id: LIVRAISONS_DOOR_ID,
      label: 'Livraisons',
      sections: false,
    },
    {
      id: MAINTENANCE_DOOR_ID,
      label: 'Maintenance',
      // Le meme catalogue de filtres : une categorie reste une tranche
      // legitime du materiel en maintenance (les compteurs du popover, eux,
      // sont calcules par le moteur sur le jeu de CETTE porte).
      filters: filterDefs,
      // Une seule stat, fixe : le jeu de la porte EST le compteur qui compte.
      stats: (live: Asset[]): ScreenStat[] => [
        { value: String(live.length), label: 'en maintenance' },
      ],
      // Pas de placeholder declare : celui-ci retombe sur le defaut du socle
      // ("Rechercher ou demander a Lia..."), et c'est voulu : les deux chemins
      // du contrat sont montres par l'app de reference.
    },
  ];

  /*
   * L'ETAT D'ECRAN, tenu par le core. Appele ICI, visible dans le fichier
   * d'ecran : quand une vue se comporte mal, on lit l'etat la ou il est cree.
   */
  const screen = useScreen<Asset>({
    appId: INVENTAIRE_APP_ID,
    screenId: INVENTAIRE_SCREEN_ID,
    // LE CHARGEUR EST PARTAGE par les trois portes : elles se distinguent par
    // `query.section` (l'id de porte), exactement comme les sections d'avant.
    // C'est le moteur qui rappelle ce chargeur quand la porte change. La porte
    // Livraisons ne prend pas de chargeur a elle : le parc doit etre lu meme
    // quand on ARRIVE sur elle (lien `?s=livraisons`), sinon les compteurs du
    // menu resteraient a zero.
    load: async (query) => {
      /*
       * LE MODE ARCHIVES voyage dans la requete (`query.archivesMode`) et c'est
       * l'ECRAN qui le traduit en option de SDK. On relaie `'all'` et non
       * `'only'`, et c'est un choix, pas un raccourci : cet ecran est en
       * pagination CLIENTE (le chargeur rend tout) et TOUS ses chiffres se
       * calculent sur ce qu'il a charge, y compris les trois compteurs du menu
       * ci-dessous. Avec `'only'`, le parc actif quitterait la memoire et ces
       * compteurs tomberaient a zero le temps du mode : exactement ce que la
       * regle G15 interdit (ils portent sur la porte BRUTE, donc sur les
       * actifs). Avec `'all'`, le moteur fait le partage : les archives a
       * l'ecran, les actifs dans les compteurs et les stats.
       *
       * Ce qu'on n'appelle JAMAIS : `count({ archived: 'only' | 'all' })`.
       * Cette agregation ne lit aucun document, donc elle ne peut pas retirer
       * les SUPPRIMES que le SDK retire cote client : le total depasserait la
       * liste (raison complete en tete de `packages/sdk/src/list-query.ts`).
       */
      const all = await sdk.assets.list({ archived: query.archivesMode ? 'all' : 'exclude' });
      // FILTRE SYSTEMATIQUE : seuls les `materiel` nous appartiennent.
      const parcComplet = all.filter(isMateriel);
      // Les compteurs du menu disent la meme verite que la zone 5 hors mode
      // Archives : les archives en sont exclues, dans les DEUX modes.
      setParc(parcComplet.filter((asset) => asset.archivedAt == null));
      if (query.section === LIVRAISONS_DOOR_ID) return [];
      return query.section === MAINTENANCE_DOOR_ID
        ? parcComplet.filter((asset) => statusOf(asset) === 'maintenance')
        : parcComplet;
    },
    getId: (asset) => asset.id,
    // Entree a froid (reprise de session sur une fiche) : on charge le materiel
    // seul plutot que toute la collection. Meme filtre systematique qu'en
    // liste : un asset d'une autre app ne s'affiche jamais en fiche ici.
    get: async (id) => {
      const asset = await sdk.assets.get(id);
      return asset != null && isMateriel(asset) ? asset : null;
    },
    /*
     * LES TROIS GESTES DE FICHE (chantier 2_0), declares au niveau ECRAN et
     * non porte par porte : les trois portes montrent le MEME materiel
     * (elles partagent deja `load` et `get` pour cette raison), donc archiver
     * un exemplaire depuis Maintenance ou depuis Materiel est le meme geste
     * sur le meme document. Le moteur en fait les entrees du menu "..." de la
     * rangee 3c avec leurs fenetres, la fermeture de la fiche et le
     * rechargement ; l'app ne fait qu'ECRIRE, via le SDK.
     *
     * Une porte peut declarer les siens et il gagne alors sur celui-ci, comme
     * pour `load` : c'est le meme repli, et l'app de reference montre ici le
     * chemin ECRAN quand `contact` montre le chemin PORTE.
     *
     * `remove` est la suppression du socle (trois etats : `archivedAt` +
     * `deletedAt`), jamais un delete dur : le SDK ne l'expose meme pas.
     */
    archive: async (asset: Asset) => {
      await sdk.assets.archive(asset.id);
    },
    unarchive: async (asset: Asset) => {
      await sdk.assets.unarchive(asset.id);
    },
    remove: async (asset: Asset) => {
      await sdk.assets.remove(asset.id);
    },
    types: INVENTAIRE_VIEW_TYPES,
    bindings,
    doors,
    defaultView: 'core.liste',
    defaultSection: MATERIEL_DOOR_ID,
  });

  const { markRead, reload, removeFilter } = screen;
  const activeFilterIds = screen.activeFilterIds;

  // Shortcut du dock (action `new` de la fiche taki.app.json) : on ouvre le
  // MEME Modal que l'action de la porte, puis on marque l'action traitee.
  const { actionId: pendingActionId, consume: consumePendingAction } =
    usePendingAppAction('inventaire');

  useEffect(() => {
    if (pendingActionId == null) return;
    if (pendingActionId === 'new') openCreate();
    consumePendingAction();
  }, [pendingActionId, consumePendingAction, openCreate]);

  // Si un filtre ACTIF vient d'etre masque dans les reglages, il est RETIRE
  // (jamais de tag actif que le catalogue ne propose plus). Les autres filtres
  // poses survivent : on retire l'entree disparue, pas l'etat entier.
  useEffect(() => {
    for (const id of activeFilterIds) {
      if (filterVisibility[id] === false) removeFilter(id);
    }
  }, [activeFilterIds, filterVisibility, removeFilter]);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setFormError(null);
      setSaving(true);
      try {
        const name = draft.name.trim();
        const reference = draft.reference.trim();
        // Les champs standard (id, dates, createdBy, schemaVersion...) sont
        // remplis par le SDK. Un materiel qui entre au parc est disponible. La
        // reference n'est envoyee que si elle est renseignee : le socle exige
        // une chaine non vide sur ce champ natif.
        const created = await sdk.assets.create({
          orgId: config.orgId,
          type: MATERIEL_TYPE,
          name,
          status: 'disponible',
          ...(reference === '' ? {} : { reference }),
          customFields: customFieldsFromDraft(draft),
        });
        // Modele de la boite mail : un message qu'on a envoye soi-meme ne
        // s'affiche pas comme non lu. On marque donc la fiche AVANT le
        // rechargement, sinon le materiel qu'on vient de saisir revient du
        // serveur avec son lisere et sa pastille.
        markRead(created.id);
        // Les donnees appartiennent au core : on lui demande de recharger
        // plutot que de tenir une copie locale.
        reload();
        closeModal();
      } catch (cause) {
        setFormError(
          cause instanceof Error ? cause.message : "Echec de l'enregistrement du materiel.",
        );
      } finally {
        setSaving(false);
      }
    },
    [draft, sdk, config.orgId, markRead, reload, closeModal],
  );

  /* ---- Zone 1 : le menu des portes, compteurs sur le jeu COMPLET ---- */

  const menuItems = useMemo(
    () => [
      { id: MATERIEL_DOOR_ID, label: 'Materiel', count: parc.length },
      // Le jeu de la porte Livraisons est vide par construction (voir sa
      // declaration) : son compteur gele l'est aussi.
      { id: LIVRAISONS_DOOR_ID, label: 'Livraisons', count: 0 },
      {
        id: MAINTENANCE_DOOR_ID,
        label: 'Maintenance',
        count: parc.filter((asset) => statusOf(asset) === 'maintenance').length,
      },
    ],
    [parc],
  );

  /*
   * Le libelle de la puce de sortie de la rangee 3 : le nom de la vue
   * collection ou la sortie de fiche ramene ("Liste", "Tableau"...). L'ecran
   * ne teste AUCUN niveau, c'est le gabarit qui le deduit de `views` +
   * `activeViewId` : conditionner ici recreerait le bug du bouton "Modifier"
   * re-code partout.
   */
  const exitLabel = screen.views.find((view) => view.id === screen.lastCollectionViewId)?.label;

  // L'etat vide suit la porte : celui de la porte Maintenance n'invite pas a
  // creer (elle n'a pas d'ajout), il dit par ou un materiel y entre ; celui de
  // la porte Livraisons dit ce qu'elle attend (aucun bouton a citer : la porte
  // ne declare pas d'action).
  const emptyState =
    screen.door.id === MAINTENANCE_DOOR_ID
      ? {
          title: 'Aucun materiel en maintenance',
          hint: 'Un materiel entre ici quand sa disponibilite passe a "En maintenance", depuis le kanban ou sa fiche.',
        }
      : screen.door.id === LIVRAISONS_DOOR_ID
        ? {
            title: "Aucune livraison pour l'instant",
            hint: 'Le suivi des livraisons du parc ouvrira ici, dans une prochaine version de l\'app.',
          }
        : {
            title: "Aucun materiel pour l'instant",
            hint: 'Clique sur "Ajouter", ou importe le jeu de demonstration depuis les Parametres.',
          };

  return (
    <>
      <ScreenLayout
        /* Zone 1 : identite de l'app (1a) et menu des portes (1b). Le rail
           s'arrete au menu : plus d'actions ni de zone meta dedans. */
        appName="Inventaire"
        appIcon={<IconCameraVideo size={18} />}
        menuItems={menuItems}
        activeMenuId={screen.section ?? MATERIEL_DOOR_ID}
        onMenuSelect={screen.setSection}
        /* Zone 2a : le h1 affiche le label de la porte active ("Materiel",
           "Maintenance") ; `title` reste le repli. */
        title="Inventaire"
        /* Zone 2c : les stats de la porte, resolues par le moteur (pref lue
           dans la declaration de la porte, valeurs sur le jeu vivant). */
        headerStats={screen.door.stats}
        /* Zone 2b : la recherche, centree sur l'ecran, placeholder de la
           porte. Le core filtre, pas l'ecran. */
        onSearchChange={screen.setSearch}
        searchValue={screen.query.search}
        searchPlaceholder={screen.door.searchPlaceholder}
        /* Zone 3a : les actions de la porte (regle "+ Ajouter" tenue par le
           chassis) puis celles que la vue active propose, deja forcees en
           secondaire par le moteur. */
        quickActions={screen.door.actions}
        viewActions={screen.viewActions}
        /* Zones 3b + 4 : outils (filtre, tri, etoile), tags et favoris. L'etat
           du moteur se passe TEL QUEL ; le catalogue de tri, lui, est une
           declaration d'ecran. */
        tools={screen}
        sortOptions={SORT_OPTIONS}
        /* Zone 3c : le selecteur de vues, epingle, JAMAIS conditionnel. Le
           calendrier y est GRISE avec sa raison (un materiel n'a pas de date
           metier), jamais absent. */
        views={screen.views}
        activeViewId={screen.viewId}
        onViewSelect={screen.setView}
        /* Rangee 3 au niveau FICHE : a gauche la puce de sortie (qui porte
           aussi le raccourci `Echap`) puis l'indicateur d'enregistrement avec
           sa relance en cas d'echec ; a droite, a la place du selecteur de
           vues, le curseur de fiche. Les six props sont passees SANS
           condition : le gabarit ne les rend qu'au niveau `record`, et une
           fiche sans elles serait une nasse (aucune sortie, aucun retour sur
           l'ecriture, aucun voisin). */
        onRecordExit={() => screen.openRecord(null)}
        recordExitLabel={exitLabel}
        saveState={screen.saveState}
        onRetrySave={screen.retrySave}
        recordCursor={screen.cursor}
        onRecordNavigate={screen.openRecord}
        /* Le menu "..." du groupe epingle (3c) : Archiver / Desarchiver et
           Supprimer. RIEN A CABLER ICI : `tools={screen}` plus haut les porte
           deja (`recordMenuActions`), construits par le moteur depuis les
           verbes declares plus haut. */
        /* La zone 5 est PRETEE au ViewHost et aux vues. */
        scrollRef={screen.scrollRef}
      >
        {/* Zone 5 pilotee par une VUE. Le ViewHost tient les etats standard
            dans la meme priorite que le gabarit (erreur > chargement > fiche
            introuvable > vide > contenu), la memoire de defilement par vue et
            le filet anti-plantage. L'etat vide suit la porte et ne depend
            JAMAIS du formulaire : le Modal flotte au-dessus. */}
        <ViewHost
          state={screen}
          errorTitle="Echec du chargement du parc"
          retryLabel="Reessayer"
          emptyTitle={emptyState.title}
          emptyHint={emptyState.hint}
          notFoundTitle="Materiel introuvable"
          notFoundHint="Ce materiel a ete supprime, ou tu n'y as pas acces."
        />
      </ScreenLayout>

      {/* La CREATION, dans un Modal FRERE du ScreenLayout : il flotte au-dessus
          de tous les etats de la zone 5 (vide compris). L'EDITION, elle, n'a
          plus de Modal du tout : elle se fait en place sur la fiche. */}
      <Modal
        open={createOpen}
        onClose={closeModal}
        title="Nouveau materiel"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saving}>
              Annuler
            </Button>
            {/* Soumission du formulaire du corps via l'attribut natif `form`
                (le footer vit hors de l'element form). `loading` rend le
                spinner du socle dans le bouton, largeur stable. */}
            <Button
              variant="primary"
              type="submit"
              form="inventaire-materiel-form"
              loading={saving}
            >
              Creer le materiel
            </Button>
          </>
        }
      >
        <form id="inventaire-materiel-form" onSubmit={onSubmit} className="grid grid-cols-2 gap-3">
          <Field label="Nom du materiel" wide>
            <Input
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              placeholder="Sony FX9 n°3"
              required
            />
          </Field>
          <Field label="Categorie">
            <select
              className={SELECT_CLASSES}
              value={draft.category}
              onChange={(event) => setDraft({ ...draft, category: event.target.value })}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          {/* Le champ natif `reference` du socle n'accepte qu'une chaine non
              vide et le SDK ne sait pas l'effacer : on le dit ICI, au seul
              moment ou la case est encore libre. */}
          <Field
            label="Reference (n° de serie)"
            hint="Elle pourra etre corrigee sur la fiche, jamais effacee."
          >
            <Input
              value={draft.reference}
              onChange={(event) => setDraft({ ...draft, reference: event.target.value })}
              placeholder="SNY-FX9-003"
            />
          </Field>
          <Field label="Marque">
            <Input
              value={draft.brand}
              onChange={(event) => setDraft({ ...draft, brand: event.target.value })}
              placeholder="Sony"
            />
          </Field>
          <Field label="Modele">
            <Input
              value={draft.model}
              onChange={(event) => setDraft({ ...draft, model: event.target.value })}
              placeholder="PXW-FX9"
            />
          </Field>
          <Field label="Date d'achat">
            {/* Date PURE : stockee en chaine "YYYY-MM-DD", jamais en Timestamp
                a minuit (le jour glisserait d'un fuseau a l'autre). */}
            <Input
              type="date"
              value={draft.purchaseDate}
              onChange={(event) => setDraft({ ...draft, purchaseDate: event.target.value })}
            />
          </Field>
          <Field label="Prix d'achat (EUR)">
            <Input
              type="number"
              min="0"
              step="1"
              value={draft.purchasePrice}
              onChange={(event) => setDraft({ ...draft, purchasePrice: event.target.value })}
              placeholder="18500"
            />
          </Field>
          <Field label="Tarif journalier (EUR)">
            <Input
              type="number"
              min="0"
              step="1"
              value={draft.dailyRate}
              onChange={(event) => setDraft({ ...draft, dailyRate: event.target.value })}
              placeholder="290"
            />
          </Field>
          <Field label="Etat physique">
            <select
              className={SELECT_CLASSES}
              value={draft.condition}
              onChange={(event) => setDraft({ ...draft, condition: event.target.value })}
            >
              {CONDITION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Emplacement de stockage" wide>
            <Input
              value={draft.storage}
              onChange={(event) => setDraft({ ...draft, storage: event.target.value })}
              placeholder="Rack A1"
            />
          </Field>
          {formError != null ? (
            <p className="col-span-2 text-sm text-danger" role="alert">
              {formError}
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  );
}

export default InventaireScreen;
